"use client"

import { useState, useEffect } from "react"
import { Copy, Users, Award, ChevronUp, ChevronRight, Network, UserPlus, Layers, Share2 } from "lucide-react"
import { Tooltip, CircularProgress, Avatar, Chip, Snackbar, Alert } from "@mui/material"
import { useSelector, useDispatch } from "react-redux"
import { userSelect, homeScreenStatSelect, allRemoteSettingsSelect } from "../../../redux/selectors/index"
import { authActions } from "../../../redux/actions"
import { capitalizeFirstLetter, niceDate, money } from "../../../utility/Helper1"
import WrapUpP2p from "../p2p/layouts/WrapUpP2p"

export default function AffiliateDashboard() {
  const dispatch = useDispatch()
  const site_settings = useSelector(allRemoteSettingsSelect)
  const userData = useSelector(userSelect)
  const finance = useSelector(homeScreenStatSelect)

  const [isLoading, setIsLoading] = useState(true)
  const [showUpline, setShowUpline] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState("")
  const [snackbarSeverity, setSnackbarSeverity] = useState("success")
  const [viewingLevel, setViewingLevel] = useState(1)
  const [refData, setRefData] = useState([])
  const [refDataRaw, setRefDataRaw] = useState([])
  const [mainUplineData, setMainUplineData] = useState({})
  const [lengthToShow, setLengthToShow] = useState(20)
  const [activeTab, setActiveTab] = useState("downlines")

  const myRefCode = userData?.myrefid
  const myRefLink = window.location.origin + "/#/signup?ref=" + myRefCode

  const loadDownLines = (myrefid) => {
    setIsLoading(true)
    dispatch(
      authActions.onRefreshSystemData(`user/referral/${myrefid}`, 10, {}, (response) => {
        setIsLoading(false)
        setRefDataRaw(response?.data)
        setRefData(response?.data?.referrals)
        setMainUplineData(response?.data?.upline)
      }),
    )
  }

  useEffect(() => {
    loadDownLines(userData?.myrefid)
  }, [])

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(myRefLink)
      .then(() => {
        setSnackbarMessage("Referral link copied to clipboard!")
        setSnackbarSeverity("success")
        setSnackbarOpen(true)
      })
      .catch(() => {
        setSnackbarMessage("Failed to copy referral link")
        setSnackbarSeverity("error")
        setSnackbarOpen(true)
      })
  }

  const showMore = () => {
    setLengthToShow((el) => el + 20)
  }

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false)
  }

  // Get data for the current viewing level
  const currentLevelData = refData?.["level" + viewingLevel] || []
  const allReferralData = currentLevelData?.slice(0, lengthToShow)

  // Calculate total counts for each level
  const level1Count = refData?.level1?.length || 0
  const level2Count = refData?.level2?.length || 0
  const level3Count = refData?.level3?.length || 0

  // Calculate total earnings
  const commissionAmount = finance?.[0]?.commission ?? 0
  const hasCommission = commissionAmount > 0
  const currencyType = finance?.[0]?.type

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <CircularProgress />
      </div>
    )
  }

  return (
    <WrapUpP2p>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800">Affiliate Dashboard</h1>
            <p className="text-gray-600 mt-2">Manage your referrals and track your earnings</p>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Total Referrals Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200/75 p-6 flex items-center hover:shadow-2xl transition-shadow duration-300 ease-in-out">
              <div className="flex-shrink-0 rounded-xl bg-gradient-to-tr from-purple-500 to-indigo-600 text-white shadow-lg h-14 w-14 flex items-center justify-center mr-5">
                <Users className="h-7 w-7" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600">Total Referrals</p>
                <p className="text-3xl font-extrabold text-gray-900 mt-1">{level1Count + level2Count + level3Count}</p>
              </div>
            </div>

            {/* Level 1 Referrals */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200/75 p-6 flex items-center hover:shadow-2xl transition-shadow duration-300 ease-in-out">
              <div className="flex-shrink-0 rounded-xl bg-gradient-to-tr from-blue-500 to-cyan-600 text-white shadow-lg h-14 w-14 flex items-center justify-center mr-5">
                <UserPlus className="h-7 w-7" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600">Direct Referrals</p>
                <p className="text-3xl font-extrabold text-gray-900 mt-1">{level1Count}</p>
              </div>
            </div>

            {/* Network Size */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200/75 p-6 flex items-center hover:shadow-2xl transition-shadow duration-300 ease-in-out">
              <div className="flex-shrink-0 rounded-xl bg-gradient-to-tr from-pink-500 to-rose-600 text-white shadow-lg h-14 w-14 flex items-center justify-center mr-5">
                <Network className="h-7 w-7" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600">Network Size</p>
                <p className="text-3xl font-extrabold text-gray-900 mt-1">{level2Count + level3Count}</p>
                <p className="text-xs text-gray-500 mt-1">Level 2 & 3</p>
              </div>
            </div>

            {/* Total Earnings Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200/75 p-6 flex items-center hover:shadow-2xl transition-shadow duration-300 ease-in-out">
              <div className="flex-shrink-0 rounded-xl bg-gradient-to-tr from-emerald-500 to-green-600 text-white shadow-lg h-14 w-14 flex items-center justify-center mr-5">
                <Award className="h-7 w-7" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600">Total Earnings</p>
                <p className="text-3xl font-extrabold text-gray-900 mt-1">
                  {money(commissionAmount, currencyType, { removeSymbol: true })}
                </p>
              </div>
            </div>
          </div>

          {/* Main Content Tabs */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
            <div className="border-b border-gray-200">
              <div className="flex">
                <button
                  onClick={() => setActiveTab("downlines")}
                  className={`px-6 py-4 text-sm font-medium ${
                    activeTab === "downlines"
                      ? "text-purple-600 border-b-2 border-purple-600"
                      : "text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Users className="inline-block h-4 w-4 mr-2" />
                  Downlines
                </button>
                <button
                  onClick={() => setActiveTab("referral")}
                  className={`px-6 py-4 text-sm font-medium ${
                    activeTab === "referral"
                      ? "text-purple-600 border-b-2 border-purple-600"
                      : "text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Share2 className="inline-block h-4 w-4 mr-2" />
                  Referral Link
                </button>
                
              </div>
            </div>

            <div className="p-6">
              {activeTab === "downlines" && (
                <>
                  {/* Level Selector */}
                  <div className="mb-6">
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Your Downlines</h2>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setViewingLevel(1)}
                        className={`relative px-5 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                          viewingLevel === 1
                            ? "bg-purple-600 text-white shadow-md"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        Level 1
                        <span className="absolute -top-2 -right-2 flex items-center justify-center w-6 h-6 bg-purple-100 text-purple-800 text-xs font-bold rounded-full border-2 border-white">
                          {level1Count}
                        </span>
                      </button>
                      <button
                        onClick={() => setViewingLevel(2)}
                        className={`relative px-5 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                          viewingLevel === 2
                            ? "bg-purple-600 text-white shadow-md"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        Level 2
                        <span className="absolute -top-2 -right-2 flex items-center justify-center w-6 h-6 bg-purple-100 text-purple-800 text-xs font-bold rounded-full border-2 border-white">
                          {level2Count}
                        </span>
                      </button>
                      <button
                        onClick={() => setViewingLevel(3)}
                        className={`relative px-5 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                          viewingLevel === 3
                            ? "bg-purple-600 text-white shadow-md"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        Level 3
                        <span className="absolute -top-2 -right-2 flex items-center justify-center w-6 h-6 bg-purple-100 text-purple-800 text-xs font-bold rounded-full border-2 border-white">
                          {level3Count}
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Level Description */}
                  <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-100">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        <Layers className="h-5 w-5 text-purple-600" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-purple-800">
                          {viewingLevel === 1 && "Direct Referrals (Level 1)"}
                          {viewingLevel === 2 && "Second Tier Referrals (Level 2)"}
                          {viewingLevel === 3 && "Third Tier Referrals (Level 3)"}
                        </h3>
                        <p className="mt-1 text-sm text-purple-700">
                          {viewingLevel === 1 && "These are users who directly signed up using your referral link."}
                          {viewingLevel === 2 && "These are users who were referred by your direct referrals."}
                          {viewingLevel === 3 && "These are users who were referred by your level 2 referrals."}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Downlines Table */}
                  {allReferralData.length > 0 ? (
                    <div className="overflow-x-auto rounded-xl border border-gray-200">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              User Details
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Status
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Reward Earned
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {allReferralData.map((downline) => {
                            const refWithdrawnFromMe = downline?.[`refWithdrawnFromMe_${viewingLevel}`] ?? 0
                            const rebWithdrawnFromMe = downline?.[`rebateWithdrawnFromMe_${viewingLevel}`] ?? 0
                            const totalEarning = refWithdrawnFromMe + rebWithdrawnFromMe

                            return (
                              <tr key={downline.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4">
                                  <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                      <Avatar
                                        src={downline?.avatar}
                                        alt={downline?.username}
                                        sx={{
                                          width: 42,
                                          height: 42,
                                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                        }}
                                      />
                                    </div>
                                    <div className="ml-4">
                                      <div className="flex items-center">
                                        <p className="text-sm font-semibold text-gray-900">
                                          {capitalizeFirstLetter(downline?.username)}
                                        </p>
                                      </div>
                                      <div className="flex items-center mt-1">
                                        <svg
                                          className="w-3 h-3 text-gray-400 mr-1"
                                          fill="currentColor"
                                          viewBox="0 0 20 20"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                        <p className="text-xs text-gray-500">{niceDate(downline?.date)}</p>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Active
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right">
                                  <div className="flex flex-col items-end">
                                    <span className="text-sm font-medium text-gray-900">
                                      {money(totalEarning, finance?.[0]?.type)}
                                    </span>
                                    {totalEarning > 0 && (
                                      <span className="text-xs text-green-600 mt-1">
                                        +{money(totalEarning, finance?.[0]?.type, { removeSymbol: true })} earned
                                      </span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                        <Users className="h-8 w-8 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No downlines found</h3>
                      <p className="text-gray-500 max-w-md mx-auto">
                        {viewingLevel === 1
                          ? "You don't have any direct referrals yet. Share your referral link to start growing your network."
                          : `You don't have any level ${viewingLevel} referrals yet. As your network grows, they'll appear here.`}
                      </p>
                    </div>
                  )}

                  {/* Load More Button */}
                  {currentLevelData.length > lengthToShow && (
                    <div className="mt-6 flex justify-center">
                      <button
                        onClick={showMore}
                        className="px-6 py-2.5 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg font-medium hover:from-purple-600 hover:to-indigo-700 transition-all shadow-sm hover:shadow-md"
                      >
                        Load More Downlines
                      </button>
                    </div>
                  )}
                </>
              )}

              {activeTab === "referral" && (
                <div>
                  <h2 className="text-xl font-bold text-gray-800 mb-4">Your Referral Link</h2>
                  <div className="p-6 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-100 mb-6">
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="flex-grow bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between shadow-sm">
                        <div className="truncate text-gray-700 font-medium">{myRefLink}</div>
                        <Tooltip title="Copy to clipboard">
                          <button
                            onClick={copyToClipboard}
                            className="ml-2 p-2 rounded-full hover:bg-gray-100 transition-colors text-purple-600 hover:text-purple-800"
                          >
                            <Copy className="h-5 w-5" />
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                    <div className="mt-6">
                      <h3 className="font-medium text-gray-900 mb-2">How it works</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 text-purple-600 mb-3">
                            <span className="font-bold">1</span>
                          </div>
                          <h4 className="font-medium text-gray-900 mb-1">Share Your Link</h4>
                          <p className="text-sm text-gray-600">
                            Share your unique referral link with friends and on social media.
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 text-purple-600 mb-3">
                            <span className="font-bold">2</span>
                          </div>
                          <h4 className="font-medium text-gray-900 mb-1">They Sign Up</h4>
                          <p className="text-sm text-gray-600">
                            When someone uses your link to sign up, they become your referral.
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 text-purple-600 mb-3">
                            <span className="font-bold">3</span>
                          </div>
                          <h4 className="font-medium text-gray-900 mb-1">Earn Rewards</h4>
                          <p className="text-sm text-gray-600">
                            Earn commissions from their activity and grow your network.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Withdrawal Section */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">Withdraw Your Earnings</h3>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Available Balance</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {money(commissionAmount, currencyType, { removeSymbol: false })}
                        </p>
                      </div>
                      {hasCommission ? (
                        <a
                          href="/withdraw/earning"
                          className="inline-flex items-center justify-center px-5 py-2.5 border border-transparent text-sm font-semibold rounded-lg shadow-md text-white bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-all duration-150 ease-in-out transform hover:scale-105"
                        >
                          Withdraw Funds
                          <ChevronRight className="ml-2 -mr-1 h-4 w-4" />
                        </a>
                      ) : (
                        <span
                          className="inline-flex items-center justify-center px-5 py-2.5 border border-gray-300 text-sm font-semibold rounded-lg text-gray-400 bg-gray-100 cursor-not-allowed shadow-sm"
                          title="No commission available to withdraw"
                        >
                          Withdraw Funds
                          <ChevronRight className="ml-2 -mr-1 h-4 w-4" />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}


            </div>
          </div>
        </div>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: "100%" }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </div>
    </WrapUpP2p>
  )
}
